// ATS Resume Scorer — analyzes resume against a job description.
// Uses profile resume by default, option to upload a different one.

import { useState } from "react";
import {
  FileSearch,
  Loader2,
  Upload,
  User,
  FileText,
  X,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { UserProfile } from "@/lib/storage";
import { toast } from "sonner";
import { sanitizeAiInput } from "@/lib/sanitize";
import AtsScoreCard, { type AtsResult } from "./AtsScoreCard";

interface Props {
  profile: UserProfile;
}

type ResumeSource = "profile" | "upload";

export default function AtsResumeTab({ profile }: Props) {
  const [jobDescription, setJobDescription] = useState("");
  const [resumeSource, setResumeSource] = useState<ResumeSource>("profile");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<AtsResult | null>(null);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedResumeData, setUploadedResumeData] =
    useState<Partial<UserProfile> | null>(null);
  const [parsing, setParsing] = useState(false);

  const profileHasResume =
    Boolean(profile.summary) ||
    profile.experience.length > 0 ||
    profile.skills.length > 0;

  const handleFileUpload = async (file: File) => {
    setUploadedFile(file);
    setParsing(true);

    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "pdf";
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      const storagePath = `${userData.user.id}/ats_temp_${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("resumes")
        .upload(storagePath, file, { upsert: true });

      if (uploadError) throw new Error(uploadError.message);

      const { data, error } = await supabase.functions.invoke("parse-resume", {
        body: { filePath: storagePath },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setUploadedResumeData(data as Partial<UserProfile>);
      toast.success("Resume parsed — ready to analyze");
    } catch (err: unknown) {
      console.error("Upload parse error:", err);
      const message = err instanceof Error ? err.message : "Failed to parse uploaded resume";
      toast.error(message);
      setUploadedFile(null);
    } finally {
      setParsing(false);
    }
  };

  const handleAnalyze = async () => {
    if (!jobDescription.trim()) {
      toast.error("Paste a job description first");
      return;
    }

    const { cleaned, flagged } = sanitizeAiInput(jobDescription);
    if (flagged) {
      toast.error("Some content was removed for security.");
    }

    const resumeData =
      resumeSource === "upload" && uploadedResumeData
        ? uploadedResumeData
        : profile;

    if (resumeSource === "profile" && !profileHasResume) {
      toast.error("Your profile doesn't have enough data. Upload a resume or add experience.");
      return;
    }

    if (resumeSource === "upload" && !uploadedResumeData) {
      toast.error("Upload a resume first or switch to your profile resume.");
      return;
    }

    setAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("score-resume", {
        body: { jobDescription: cleaned, resumeData },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setResult(data as AtsResult);
    } catch (err: unknown) {
      console.error("ATS analysis error:", err);
      const message = err instanceof Error ? err.message : "Failed to analyze resume";
      toast.error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h3 className="font-display font-bold text-lg text-foreground">
          ATS Resume Scorer
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Check how well your resume matches a job description. Get a score, see
          what&apos;s missing, and get suggestions to improve.
        </p>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground mb-2 block">
          Resume to analyze
        </Label>
        <div className="flex rounded-lg bg-muted p-1 gap-1">
          <button
            type="button"
            onClick={() => setResumeSource("profile")}
            className={`flex-1 flex items-center justify-center gap-2 text-xs font-medium py-2 rounded-md transition-all ${
              resumeSource === "profile"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground"
            }`}
          >
            <User className="w-3.5 h-3.5" />
            My Profile Resume
          </button>
          <button
            type="button"
            onClick={() => setResumeSource("upload")}
            className={`flex-1 flex items-center justify-center gap-2 text-xs font-medium py-2 rounded-md transition-all ${
              resumeSource === "upload"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground"
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            Upload Different
          </button>
        </div>
      </div>

      {resumeSource === "profile" && (
        <div
          className={`rounded-lg border p-3 text-xs ${
            profileHasResume
              ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300"
              : "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
          }`}
        >
          {profileHasResume ? (
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span>
                Using your profile data:{" "}
                {[
                  profile.summary && "summary",
                  profile.experience.length > 0 &&
                    `${profile.experience.length} experience(s)`,
                  profile.education.length > 0 &&
                    `${profile.education.length} education`,
                  profile.skills.length > 0 &&
                    `${profile.skills.length} skills`,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>
                Your profile doesn&apos;t have enough data yet. Upload a resume in your
                Profile tab or switch to &quot;Upload Different&quot; above.
              </span>
            </div>
          )}
        </div>
      )}

      {resumeSource === "upload" && (
        <div>
          {!uploadedFile ? (
            <label className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                Drop a resume or click to browse
              </p>
              <p className="text-xs text-muted-foreground">PDF, DOCX, or TXT</p>
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFileUpload(file);
                }}
              />
            </label>
          ) : (
            <div className="rounded-lg border border-border p-3 flex items-center gap-3">
              <FileText className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {uploadedFile.name}
                </p>
                {parsing && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Parsing...
                  </p>
                )}
                {uploadedResumeData && !parsing && (
                  <p className="text-xs text-primary mt-0.5">
                    Ready to analyze
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setUploadedFile(null);
                  setUploadedResumeData(null);
                }}
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      <div>
        <Label className="text-xs text-muted-foreground">Job Description</Label>
        <Textarea
          placeholder="Paste the job description here..."
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          className="mt-1 min-h-[120px]"
        />
      </div>

      <Button
        onClick={() => void handleAnalyze()}
        disabled={
          !jobDescription.trim() ||
          analyzing ||
          (resumeSource === "upload" && !uploadedResumeData) ||
          (resumeSource === "profile" && !profileHasResume)
        }
        className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
      >
        {analyzing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Analyzing...
          </>
        ) : (
          <>
            <FileSearch className="w-4 h-4" /> Analyze Resume
          </>
        )}
      </Button>

      {result && <AtsScoreCard result={result} />}
    </div>
  );
}
